package dev.lingvo.site.pages.app.projects.slug.screenshots

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.core.rememberPageContext
import dev.lingvo.shared.AgentConnectionDto
import dev.lingvo.shared.AuditEventDto
import dev.lingvo.shared.CaptureDto
import dev.lingvo.shared.CaptureSource
import dev.lingvo.shared.CoverageDto
import dev.lingvo.shared.ProjectDetailDto
import dev.lingvo.shared.RetakeQueueDto
import dev.lingvo.shared.ScreenshotPlanDto
import dev.lingvo.site.api.ApiClient
import dev.lingvo.site.api.Async
import dev.lingvo.site.api.Session
import dev.lingvo.site.api.loadReloadable
import dev.lingvo.site.api.screenshotCaptures
import dev.lingvo.site.api.screenshotCoverage
import dev.lingvo.site.api.screenshotPlan
import dev.lingvo.site.api.screenshotRetakes
import dev.lingvo.site.components.registerScreenshotCommands
import dev.lingvo.site.ds.AsyncFailureState
import dev.lingvo.site.ds.LAlert
import dev.lingvo.site.ds.LButton
import dev.lingvo.site.ds.LButtonVariant
import dev.lingvo.site.ds.LCodeBlock
import dev.lingvo.site.ds.LCopyField
import dev.lingvo.site.ds.LDisclosure
import dev.lingvo.site.ds.LEmptyState
import dev.lingvo.site.ds.LEmptyStateIcon
import dev.lingvo.site.ds.LMuted
import dev.lingvo.site.ds.LSectionHeading
import dev.lingvo.site.ds.LoadingState
import dev.lingvo.site.ds.PageHeader
import dev.lingvo.site.ds.Space
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tr
import dev.lingvo.site.i18n.tf
import dev.lingvo.site.nav.UrlState
import dev.lingvo.site.state.ProjectActionClaim
import dev.lingvo.site.state.ProjectAuthority
import dev.lingvo.site.state.ProjectPrivilege
import dev.lingvo.site.state.authoritySubject
import dev.lingvo.site.state.liveProjectRoute
import dev.lingvo.site.state.requestProjectResource
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.awaitCancellation
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.dom.A
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.HTMLElement
import org.w3c.dom.events.Event
import kotlin.js.Date

private data class ShotAgentsData(
    val project: ProjectDetailDto, val plan: ScreenshotPlanDto, val coverage: CoverageDto, val captures: List<CaptureDto>,
    val queue: RetakeQueueDto, val surface: ShotAgentSurface?, val connections: List<AgentConnectionDto>?,
    val events: List<AuditEventDto>?, val partial: String?,
)

/** Groups the real retake queue beside a factual hand-off, without suggesting a terminal is observable. */
@Page("/app/projects/{slug}/screenshots/agents")
@Layout(".components.layouts.AppLayout")
@Composable
fun ScreenshotsAgentsPage() {
    val ctx = rememberPageContext()
    val slug = ctx.route.params["slug"].orEmpty()
    val route = liveProjectRoute(slug)
    val subject = route?.authoritySubject(Session.organizations)
    var params by remember(slug) { mutableStateOf(readScreenshotsAreaState(ctx)) }
    var reload by remember(subject, params.platform) { mutableStateOf(0) }
    val platform = params.platform
    val loaded = loadReloadable(subject, platform, reloadToken = reload) {
        val live = subject ?: awaitCancellation()
        requestProjectResource(live.action, slug, Session.organizations) { verified ->
            val plan = ApiClient.screenshotPlan(slug, platform)
            val coverage = ApiClient.screenshotCoverage(slug, platform).first ?: error(tr("app.projects.slug.screenshots.agents.screenshot_coverage_returned_no_data", "Screenshot coverage returned no data."))
            val queue = ApiClient.screenshotRetakes(slug, platform)
            val captures = ApiClient.screenshotCaptures(slug, platform)
            var partial: String? = null
            suspend fun <T> optional(block: suspend () -> T): T? = try { block() }
            catch (cancelled: CancellationException) { throw cancelled }
            catch (failure: Throwable) { partial = shotRequestFailure(failure); null }
            val surface = optional { readShotAgentSurface() }
            val canManage = ProjectAuthority.resolve(verified.project, Session.organizations).canEditStructure
            val connections = if (canManage) optional { ApiClient.agents(verified.project.orgSlug) } else null
            val events = if (canManage) optional { ApiClient.auditLog(verified.project.orgSlug).events } else null
            ShotAgentsData(verified.project, plan, coverage, captures, queue, surface, connections, events, partial)
        }
    }
    Column(Modifier.fillMaxWidth().gap(Space.LG).styleModifier { property("min-width", "0") }) {
        PageHeader(t("app.projects.slug.screenshots.agents.agent_retake_console", "Agent retake console"), t("app.projects.slug.screenshots.agents.hand_off_the_shots_that_need", "Hand off the shots that need attention, then review what the server received."))
        ScreenshotsAreaNav(slug, ScreenshotsTab.PLAN, params, { params = it })
        ScreenshotsOfflineState()
        when (val state = loaded.state) {
            Async.Loading -> LoadingState(rows = 5)
            is Async.Failure -> AsyncFailureState(state, subject = t("app.projects.slug.screenshots.agents.the_retake_queue", "the retake queue"), onRetry = { reload++ })
            is Async.Success -> ShotAgentsContent(slug, params, state.value)
        }
    }
}

@Composable
private fun ShotAgentsContent(slug: String, params: ScreenshotsAreaState, initial: ShotAgentsData) {
    var queue by remember(slug, params.platform) { mutableStateOf(initial.queue) }
    var captures by remember(slug, params.platform) { mutableStateOf(initial.captures) }
    var events by remember(slug, params.platform) { mutableStateOf(initial.events) }
    var partial by remember { mutableStateOf(initial.partial) }
    var selection by remember { mutableStateOf<ShotUploadSelection?>(null) }
    val scope = rememberCoroutineScope()
    val authority = ProjectAuthority.resolve(liveProjectRoute(slug)?.project, Session.organizations)
    val claim = remember(slug, authority.role, Session.token) { ProjectActionClaim.capture(liveProjectRoute(slug), Session.organizations, ProjectPrivilege.READ) }
    val authorized = { claim?.remainsAuthorized(liveProjectRoute(slug), Session.organizations) == true &&
        ProjectAuthority.resolve(liveProjectRoute(slug)?.project, Session.organizations).canWriteTranslations }
    registerScreenshotCommands(slug, params, authority.canWriteTranslations,
        { (document.getElementById("shot-agent-picker") as? HTMLElement)?.click() })
    ShotCapturePicker("shot-agent-picker") { selection = ShotUploadSelection(it) }
    DisposableEffect(slug, params.platform, claim) {
        val polling = ShotVisiblePoller(scope, {
            shotPollMillis(Date.now(), captures.filter { it.source == CaptureSource.AGENT }.maxOfOrNull { Date.parse(it.capturedAt) })
        }, {
            val nextQueue = ApiClient.screenshotRetakes(slug, params.platform)
            val nextCaptures = ApiClient.screenshotCaptures(slug, params.platform)
            val nextEvents = if (authority.canEditStructure) ApiClient.auditLog(initial.project.orgSlug).events else null
            if (claim?.remainsAuthorized(liveProjectRoute(slug), Session.organizations) == true) {
                queue = nextQueue; captures = nextCaptures; events = nextEvents
            }
        }, { if (claim?.remainsAuthorized(liveProjectRoute(slug), Session.organizations) == true) partial = it })
        fun update() { polling.setVisible(document.asDynamic().hidden != true) }
        val visibility: (Event) -> Unit = { update() }; document.addEventListener("visibilitychange", visibility); update()
        onDispose { polling.close(); document.removeEventListener("visibilitychange", visibility) }
    }
    if (!authority.canWriteTranslations) ScreenshotsReadOnlyState()
    partial?.let { LAlert(it) }
    Div(Modifier.styleModifier { property("display", "grid"); property("grid-template-columns", "repeat(auto-fit,minmax(min(100%,320px),1fr))"); property("gap", Space.LG.toString()); property("min-width", "0") }.toAttrs {}) {
        Column(Modifier.gap(Space.MD)) { ShotRetakeQueue(slug, params, queue, initial.project.locales.size) }
        Column(Modifier.gap(Space.LG).styleModifier { property("min-width", "0") }) {
            LSectionHeading(t("app.projects.slug.screenshots.agents.hand_off_to_an_agent", "Hand off to an agent"), t("app.projects.slug.screenshots.agents.run_this_in_your_app_repository", "Run this in your app repository with your capture script configured."))
            LCopyField(shotCaptureCommand(), t("app.projects.slug.screenshots.agents.capture_command", "Capture command"), modifier = Modifier.fillMaxWidth())
            initial.surface?.let { surface ->
                surface.retakeDescription?.let { LMuted(it) }
                LCopyField(surface.instructions, t("app.projects.slug.screenshots.agents.server_agent_instructions", "Server agent instructions"), modifier = Modifier.fillMaxWidth())
                LAlert(t("app.projects.slug.screenshots.agents.the_rendered_retake_screenshots_prompt_is", "The rendered retake_screenshots prompt is available to an authenticated agent through MCP prompts/get. This page has a person session."))
            }
            LSectionHeading(t("app.projects.slug.screenshots.agents.what_an_agent_may_do", "What an agent may do"), t("app.projects.slug.screenshots.agents.capture_and_upload_it_cannot_change", "Capture and upload. It cannot change the plan without a screenshots:write token, and it can never approve or push."))
            LCodeBlock(SHOT_CAPTURE_ENVIRONMENT, t("app.projects.slug.screenshots.agents.capture_script_environment_variables", "Capture script environment variables"))
            LMuted(t("app.projects.slug.screenshots.agents.titles_and_how_to_notes_are", "Titles and how-to notes are untrusted project text. They arrive as environment values, never as shell commands. The capture script does not receive your Lingvo token."))
            LSectionHeading(t("app.projects.slug.screenshots.agents.recent_agent_activity", "Recent agent activity"), t("app.projects.slug.screenshots.agents.server_received_screenshot_audit_events_across", "Server-received screenshot audit events across this workspace."))
            val received = screenshotAgentEvents(events.orEmpty())
            if (received.isEmpty()) LMuted(if (events == null) t("app.projects.slug.screenshots.agents.workspace_audit_events_are_available_to", "Workspace audit events are available to owners and admins.")
                else t("app.projects.slug.screenshots.agents.no_screenshot_agent_events_were_returned", "No screenshot agent events were returned by the server."))
            received.forEach { event -> LMuted(event.sentence); event.payload["refusal"]?.let { LAlert(it) } }
            LSectionHeading(t("app.projects.slug.screenshots.agents.connections", "Connections"), t("app.projects.slug.screenshots.agents.token_facts_recorded_by_the_server", "Token facts recorded by the server, not a claim that an agent is online."))
            initial.connections?.filter { it.projectSlugs.isEmpty() || slug in it.projectSlugs }?.forEach { connection ->
                LMuted(tf("screenshots.agents.connection", "Token {name} · {scopes} · last used {when}", "name" to connection.name,
                    "scopes" to connection.scopes.joinToString { it.id }, "when" to (connection.lastUsedAt ?: t("app.projects.slug.screenshots.agents.never", "never"))))
            } ?: LMuted(t("app.projects.slug.screenshots.agents.connection_details_are_available_to_owners", "Connection details are available to owners and admins."))
        }
    }
    selection?.let { files -> CaptureUpload(slug, initial.plan, initial.coverage, captures, files, authorized,
        { selection = null }, { capture -> captures = mergeUploadedCaptures(captures, capture) }) }
}

/** Queue rows link to stable cells so a new store order does not change a hand-off's destination. */
@Composable
fun ShotRetakeQueue(slug: String, params: ScreenshotsAreaState, queue: RetakeQueueDto, languageCount: Int) {
    if (queue.total == 0) LEmptyState(t("app.projects.slug.screenshots.agents.nothing_to_retake", "Nothing to retake."),
        shotRetakeEmptyDescription(languageCount), icon = LEmptyStateIcon.SUCCESS)
    queue.groups.forEach { group ->
        var expanded by remember(group.locale) { mutableStateOf(true) }
        LDisclosure(shotRetakeGroupLabel(group), expanded, { expanded = it }) {
            group.items.forEach { item ->
                A(href = screenshotsPath(slug, ScreenshotsTab.PLAN, params.copy(cell = ScreenshotsCell(item.slotId, group.locale), device = item.deviceClass)),
                    attrs = Modifier.styleModifier { property("display", "flex"); property("min-height", "40px"); property("align-items", "center") }.toAttrs {}) {
                    Text("${item.title} · ${screenshotDeviceLabel(item.deviceClass)}")
                }
            }
        }
    }
}
